type PaginationItem = number | "ellipsis-left" | "ellipsis-right";

type PaginationProps = {
  currentPage: number;
  totalItems: number;
  pageSize: number;
  itemUnit: string;
  ariaLabel: string;
  onPageChange: (page: number) => void;
  className?: string;
};

function createPaginationItems(
  currentPage: number,
  totalPages: number
): PaginationItem[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const items: PaginationItem[] = [1];
  let start = Math.max(2, currentPage - 1);
  let end = Math.min(totalPages - 1, currentPage + 1);

  if (currentPage <= 4) {
    start = 2;
    end = 5;
  } else if (currentPage >= totalPages - 3) {
    start = totalPages - 4;
    end = totalPages - 1;
  }

  if (start > 2) items.push("ellipsis-left");
  for (let page = start; page <= end; page++) items.push(page);
  if (end < totalPages - 1) items.push("ellipsis-right");
  items.push(totalPages);

  return items;
}

export function getPaginationWindow(
  totalItems: number,
  currentPage: number,
  pageSize: number
) {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const page = Math.max(1, Math.min(currentPage, totalPages));
  const startIndex = (page - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalItems);

  return { totalPages, page, startIndex, endIndex };
}

export function Pagination({
  currentPage,
  totalItems,
  pageSize,
  itemUnit,
  ariaLabel,
  onPageChange,
  className = "",
}: PaginationProps) {
  if (totalItems <= 0) return null;

  const { totalPages, page, startIndex, endIndex } = getPaginationWindow(
    totalItems,
    currentPage,
    pageSize
  );
  const paginationItems = createPaginationItems(page, totalPages);

  return (
    <div className={`content-pagination${className ? ` ${className}` : ""}`}>
      <p aria-live="polite">
        {totalItems.toLocaleString("ja-JP")}
        {itemUnit}中　{(startIndex + 1).toLocaleString("ja-JP")}〜
        {endIndex.toLocaleString("ja-JP")}
        {itemUnit}を表示
      </p>
      {totalPages > 1 && (
        <nav aria-label={ariaLabel}>
          <button
            type="button"
            disabled={page === 1}
            onClick={() => onPageChange(page - 1)}
          >
            前へ
          </button>
          {paginationItems.map((item) =>
            typeof item === "number" ? (
              <button
                key={item}
                type="button"
                className={item === page ? "is-active" : ""}
                aria-current={item === page ? "page" : undefined}
                aria-label={`${item}ページ目`}
                onClick={() => onPageChange(item)}
              >
                {item}
              </button>
            ) : (
              <span key={item} aria-hidden="true">
                …
              </span>
            )
          )}
          <button
            type="button"
            disabled={page === totalPages}
            onClick={() => onPageChange(page + 1)}
          >
            次へ
          </button>
        </nav>
      )}
    </div>
  );
}
